import { useMemo, useRef, useState } from 'react';
import { t } from '@lingui/core/macro';
import { ChevronDown, Landmark, X } from 'lucide-react';
import { Button, Menu, MenuButton, MenuItem, MenuList, Box, Text } from '@chakra-ui/react';

import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import useFiltersContext from '@duke-hq/filters/context/useFiltersContext';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import { iconSize } from '@duke-hq/ui/constants/scalable';

export default function ProviderFilter() {
  const { data: providers } = trpcAdmin.providers.all.useQuery({});
  const { updateFilters, filters } = useFiltersContext();
  const data = useMemo(() => providers ?? [], [providers]);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const providerName = useMemo(() => {
    const provider = data.find((provider) => provider.id === filters.provider);
    return provider ? provider.name : t`Provider`;
  }, [data, filters.provider]);

  const inputRef = useRef<HTMLInputElement>(null);

  const filteredProviders = useMemo(() => {
    return data.filter(({ name }) => name.toLowerCase().includes(searchTerm.trim().toLowerCase()));
  }, [data, searchTerm]);

  return (
    <Menu initialFocusRef={inputRef} onClose={() => setSearchTerm('')}>
      <MenuButton
        as={Button}
        variant="ghost"
        size="sm"
        width="auto"
        colorScheme="gray"
        px={2}
        rightIcon={
          filters.provider !== undefined ? (
            <Box
              minW="fit-content"
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                updateFilters({
                  provider: undefined,
                  consultants: undefined,
                  sites: undefined,
                  regions: undefined,
                });
              }}
              ml={1}
            >
              <X size={iconSize.md} />
            </Box>
          ) : (
            <ChevronDown size={iconSize.md} />
          )
        }
        leftIcon={<Landmark size={iconSize.md} />}
      >
        {providerName}
      </MenuButton>
      <MenuList maxHeight="700px" overflowY="auto" p={0} minH="200px">
        <Box px={2} pt={2}>
          <SearchInput onCommit={setSearchTerm} urlSync={false} ref={inputRef} mb={2} />
        </Box>
        {filteredProviders?.length > 0 ? (
          filteredProviders.map(({ name, id }) => (
            <MenuItem
              as={Box}
              key={id}
              onClick={() => {
                updateFilters({ provider: id, consultants: undefined, sites: undefined });
              }}
              fontWeight={name === providerName ? 600 : 'normal'}
              bg={name === providerName ? 'bg.emphasis' : undefined}
            >
              {name}
            </MenuItem>
          ))
        ) : (
          <Text fontSize="sm" textAlign="center" p={3}>
            No providers found.
          </Text>
        )}
      </MenuList>
    </Menu>
  );
}
